import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, ClipboardCheck, Calendar, History, UserCheck, Scale, BarChart3, Award, MapPin, ShieldCheck, TrendingDown, FileSearch } from 'lucide-react';
const capabilities = [{
  icon: Calendar,
  name: 'Prospective Review',
  description: 'Pre-authorization of proposed treatments before care is delivered, ensuring medical necessity from day one.'
}, {
  icon: ClipboardCheck,
  name: 'Concurrent Review',
  description: 'Real-time review of ongoing care, hospital stays, and extended treatment plans to keep claims on track.'
}, {
  icon: History,
  name: 'Retrospective Review',
  description: 'Post-service review to validate medical necessity, identify overutilization, and recover unnecessary spend.'
}, {
  icon: UserCheck,
  name: 'Peer-to-Peer Review',
  description: 'Board-certified physician reviewers conduct collegial consultations with treating providers.'
}, {
  icon: Scale,
  name: 'Appeals Management',
  description: 'Structured appeals process aligned with jurisdictional requirements and clear documentation.'
}, {
  icon: FileSearch,
  name: 'Evidence-Based Guidelines',
  description: 'Reviews aligned with ODG, MTUS, ACOEM, and state-specific treatment guidelines.'
}, {
  icon: MapPin,
  name: 'Multi-State Compliance',
  description: 'Jurisdictional expertise across all 50 states, with localized workflows for California, Texas, Florida, and beyond.'
}, {
  icon: BarChart3,
  name: 'Reporting & Analytics',
  description: 'Real-time dashboards showing review volumes, certification rates, savings, and provider performance.'
}];
const benefits = [{
  icon: TrendingDown,
  title: 'Lower medical spend',
  stat: '28%',
  description: 'Average reduction in unnecessary medical treatment through evidence-based determinations.'
}];
export function UtilizationReview() {
  return <div className="bg-brand-paper" data-id="element-871" data-mp="2w9kev3oenv9y">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-872" data-mp="2w6y7b41ilfrq">
        <div className="absolute inset-0 opacity-10" data-id="element-873" data-mp="3az0249oq9ikw">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-874" data-mp="366fexr0bu3qc"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-875" data-mp="3ez0u6x4r1saf"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-876" data-mp="3tu70zmbo5v4t">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-877" data-mp="3oy83n98d52s9">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-878" data-mp="a3hjovcucqt3">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-879" data-mp="c3fwbdux4u5">
                Managed Care Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-880" data-mp="58bp785di3te">
              <span className="text-brand-gold italic" data-id="element-881" data-mp="3rie9xnkg95bz">Utilization</span> Review
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-882" data-mp="3dimztxx20939">
              Evidence-based medical necessity determinations that ensure
              injured workers get the right care — and your program avoids the
              wrong costs.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-883" data-mp="1gpl6bo114jrj">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-884" data-mp="3cszmknxg715o">
                Request a Consultation <ArrowRight className="w-5 h-5 ml-2" data-id="element-885" data-mp="33ri9lefc7fl7" />
              </Link>
              <a href="tel:7142591053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-886" data-mp="2ytgs2xxk7obu">
                <Phone className="w-5 h-5 mr-2" data-id="element-887" data-mp="2p35baass1knj" /> 714.259.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-888" data-mp="2g2pbbbb8svnf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-889" data-mp="2g3qlzeocjg4m">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-890" data-mp="2b2b23pxwnfnt">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform -rotate-2" data-id="element-891" data-mp="vkes5ipqz5lq"></div>
            <img src="https://cdn.magicpatterns.com/uploads/j9bnHHqs1SL6KwbuXHrzQz/Depositphotos_foto_black_doctor.jpg" alt="Smiling doctor with a stethoscope caring for a patient during a clinical review" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-892" data-mp="zmjlegcaa07u" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-893" data-mp="buuk4ahhi1er">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-894" data-mp="rh8syaqxikqc">
              The Problem We Solve
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-895" data-mp="mgtbb6zm6r2k">
              The right care, at the right time — backed by evidence.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-896" data-mp="w98wed67jvro">
              Our accredited UR program guides the workers' recovery path. We
              use physician reviewers and the latest evidence-based guidelines
              to make defensible, timely determinations — protecting both your
              bottom line and the worker's recovery path.
            </p>
            <ul className="space-y-4" data-id="element-897" data-mp="dh6aoneplpak">
              {['URAC-accredited workflows and clinical processes', 'Board-certified physician reviewers across every specialty', 'Jurisdictional expertise in all 50 states', 'Defensible documentation for every determination'].map(item => <li key={item} className="flex items-start" data-id="element-898" data-mp="21g09v3xfk8ee">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-899" data-mp="21hbfzaecls8o" />
                  <span className="text-gray-800 font-medium" data-id="element-900" data-mp="1mkaqldjmq9dd">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-901" data-mp="2f86rkug8enfy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-902" data-mp="2k7197t2gq5yh">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-903" data-mp="2k8cejxbk3lrp">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-904" data-mp="25gtxrxavhn7a">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-905" data-mp="2y69dv1hea6a2">
              A complete UR program.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-906" data-mp="32y14td4gqe0q">
              From pre-authorization through appeals — every step of the
              utilization review process, under one roof.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-907" data-mp="y385xwde3txo">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-908" data-mp="p44lanykr6h2" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-909" data-mp="3siqtn2mupgqx">
                    <Icon className="w-6 h-6" data-id="element-910" data-mp="3nfpyg07n044z" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-911" data-mp="3se1dqqgpb6ul">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-912" data-mp="10yc8q1ec9u6">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-913" data-mp="ez515godazhp">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-914" data-mp="f06e5qoy1w22">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-915" data-mp="jxga7p6u95yv">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-916" data-mp="2gr18sf7ezp77">
              The Arissa Difference
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-917" data-mp="2lqe6juztqgok">
              Measurable outcomes you can defend.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-918" data-mp="zq3gnl4qm0ol">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-919" data-mp="13ujj1cf56pz2" data-mpi={i}>
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-920" data-mp="21fyr99a0ygk3"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-921" data-mp="2bjma3teckmiy" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-922" data-mp="2bht0q6ccno79">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-923" data-mp="21l6gnjrul16a">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-924" data-mp="1xk4eov6jqb0m">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-925" data-mp="2pcllpkb0dwl8">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-926" data-mp="2kk26kiwoateb"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-927" data-mp="2giysa0u9x5f7">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-928" data-mp="2gh5u79xq7tit">
            Arissa brings expertise to every review.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-929" data-mp="389d3tqete5np">
            Talk to our team about how Arissa's UR program can improve outcomes
            and drive measurable savings across your book of business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-930" data-mp="1bdjs6vm7mk9c">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-931" data-mp="wkqcpa6xu44o">
              Schedule a Meeting
            </Link>
            <a href="tel:7142591053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-932" data-mp="sm94enbs6nlo">
              <Phone className="w-5 h-5 mr-2" data-id="element-933" data-mp="iouz9jvb5l8o" /> 714.259.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}